import { Component, OnInit, Output, EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AiSuggestedCustomerDetailsDto } from '../../../models/ai-invoice-voice.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { AddCustomerEndPoient } from '../../../Data/service/global.service';
import { Customer } from '../../models/customer.model';
import { companyDisplayName } from '../../../shared/utils/company-display-name';
import Swal from 'sweetalert2';

/**
 * Component for adding a customer via a modal dialog.
 */
@Component({
	selector: 'app-add-customer-modal',
	templateUrl: './add-customer-modal.component.html',
	styleUrls: ['./add-customer-modal.component.scss']
})
export class AddCustomerModalComponent implements OnInit, OnChanges {
	/**
	 * Flag to control the visibility of the modal.
	 */
	@Input() showModal: boolean = false;
	/**
	 * Flag to enable debug logging.
	 */
	@Input() debug: boolean = false;
	/**
	 * Mode for the modal: 'add' or 'update'
	 */
	@Input() mode: 'add' | 'update' = 'add';
	/**
	 * Customer data for update mode
	 */
	@Input() customerData: any = null;
	/**
	 * Optional — AI assistant suggests a display name when opening add-customer from chat.
	 */
	@Input() suggestedCustomerName: string | null = null;
	/**
	 * Full customer details extracted from an uploaded invoice image.
	 * When present, all matching form fields are prefilled automatically.
	 */
	@Input() suggestedCustomerDetails: AiSuggestedCustomerDetailsDto | null = null;
	/**
	 * When opening in add mode, pre-select this company (e.g. from create invoice form).
	 */
	@Input() defaultCompanyId: number | null = null;
	/**
	 * Event emitted when the modal is closed without saving.
	 */
	@Output() onClose = new EventEmitter<void>();
	/**
	 * Event emitted when a new customer is successfully saved.
	 */
	@Output() onSave = new EventEmitter<Customer>();

	/**
	 * The reactive form group for adding a customer.
	 */
	addCustomer!: FormGroup;
	/**
	 * Data for the customer type selection.
	 */
	customerTypeData: any[] = [];
	/**
	 * List of companies for selection.
	 */
	companiesList: any[] = [];
	/**
	 * When true, show the company dropdown. When false (exactly one company), it is hidden and that company is pre-selected.
	 */
	get showCompanySelect(): boolean {
		return (this.companiesList?.length ?? 0) !== 1;
	}
	/**
	 * The current application language (synced with TranslateService for correct translations).
	 */
	curentLang: string = 'en';

	private maximizedOnce = false;

	/**
	 * Constructs the AddCustomerModalComponent.
	 * @param fb The Angular FormBuilder service.
	 * @param service The service for HTTP requests.
	 * @param spinner The service for displaying loading spinners.
	 * @param messageService The PrimeNG MessageService for notifications.
	 * @param translate The ngx-translate service for localization.
	 */
	constructor(
		private fb: FormBuilder,
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		private messageService: MessageService,
		private translate: TranslateService
	) {
		this.initializeForm();
	}

	/**
	 * @description Angular lifecycle hook called after every check of the component's view.
	 * Used here to maximize the dialog automatically on small screens when it first opens.
	 */
	ngAfterViewChecked(): void {
		if (
			this.showModal &&
			window.innerWidth < 768 &&
			!this.maximizedOnce
		) {
			this.clickMaximizeButton();
			this.maximizedOnce = true;
		}
	}

	/**
	 * @protected
	 * @description Programmatically clicks the maximize button of the PrimeNG dialog.
	 * Used for responsive behavior on small screens.
	 */
	protected clickMaximizeButton() {
		// Note: This relies on PrimeNG's v13 internal CSS classes and might break with updates.
		const dialogEl = document.querySelector('.p-dialog') as HTMLElement;
		if (dialogEl) {
			const maximizeBtn = dialogEl.querySelector('.p-dialog-header-maximize') as HTMLElement;
			if (maximizeBtn) {
				maximizeBtn.click();
			}
		}
	}

	/**
	 * Initializes the component. Fetches customer types and sets up initial form validation.
	 * @returns void
	 */
	ngOnInit(): void {
		this.curentLang = this.translate.currentLang || localStorage.getItem('curentLang') || 'en';
		this.translate.onLangChange?.subscribe((event) => {
			this.curentLang = event.lang || this.translate.currentLang || 'en';
		});
		this.getAllCustomerType();
		this.getAllCompanies();
		this.onCustomerTypeChange();
		this.onCompanyIdChange();
		if (this.addCustomer) {
			const initialType = this.addCustomer.get('customer_type')?.value;
			this.setupInitialValidators(initialType);
		}
	}

	/**
	 * Handles changes to input properties, particularly for update mode.
	 * @param changes The changed input properties
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['customerData'] && this.customerData && this.mode === 'update') {
			this.populateFormForUpdate();
		}
		if (changes['mode'] && this.mode === 'add') {
			this.resetForm();
		}
		if (changes['showModal'] && this.showModal && this.mode === 'add' && this.addCustomer) {
			this.resetForm();
			if (this.defaultCompanyId != null) {
				this.addCustomer.get('company_id')?.setValue(this.defaultCompanyId);
				this.addCustomer.get('company_id')?.updateValueAndValidity();
			}
			this.applySuggestedCustomerNameInput();
		}
	}

	private applySuggestedCustomerNameInput(): void {
		if (!this.addCustomer) return;

		const details = this.suggestedCustomerDetails;
		if (details) {
			const name = details.name?.trim() || this.suggestedCustomerName?.trim() || '';
			const patch: Record<string, unknown> = {};
			if (name) {
				patch['customer_name_he'] = name;
				patch['customer_name_ar'] = name;
				patch['customer_name_en'] = name;
			}
			if (details.vat?.trim()) {
				patch['customer_vat_tax'] = details.vat.trim();
			}
			if (details.email?.trim()) {
				patch['customer_email'] = details.email.trim();
			}
			if (details.phone?.trim()) {
				/* Strip non-numeric characters for the 9-digit phone field. */
				const digits = details.phone.replace(/\D/g, '');
				patch['customer_phone'] = digits;
			}
			if (details.address?.trim()) {
				patch['customer_address_str'] = details.address.trim();
			}
			if (Object.keys(patch).length) {
				this.addCustomer.patchValue(patch);
			}
			return;
		}

		const raw = this.suggestedCustomerName?.trim();
		if (!raw) return;
		this.addCustomer.patchValue({
			customer_name_he: raw,
			customer_name_ar: raw,
			customer_name_en: raw,
		});
	}

	/**
	 * Initializes the reactive form structure and default values.
	 * @private
	 * @returns void
	 */
	private initializeForm(): void {
		this.addCustomer = this.fb.group({
			customer_name_he: ['', Validators.required],
			customer_name_en: [''],
			customer_name_ar: [''],
			customer_type: [6, Validators.required],
			customer_phone: ['', [Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]*$")]],
			customer_email: ['', [Validators.email]],
			show_contact_on_documents: [false],
			id_number: [''],
			customer_address_city: [''],
			customer_address_str: [''],
			customer_Building_number: [''],
			customer_address_zip_code: [''],
			customer_vat_number: ['', [Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]+$")]],
			customer_vat_tax: ['', [Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]+$")]],
			company_id: [null, Validators.required],
			// Not shown (MERCHANT-359). Kept so an edit still sends the stored customer number.
			custom_text: [''],
			// TODO: Additional fields that might be needed later (commented out from old modal)
			// customer_address_str_ar: [''],
			// customer_address_str_en: [''],
			// customer_address_city_ar: [''],
			// customer_address_city_en: [''],
		});
		this.setupInitialValidators(6);
	}

	/**
	 * Resets the form to its initial state and clears validation errors.
	 * @private
	 * @returns void
	 */
	private resetForm(): void {
		this.addCustomer.reset({
			customer_name_he: '',
			customer_name_en: '',
			customer_name_ar: '',
			customer_type: 6,
			customer_phone: '',
			customer_email: '',
			show_contact_on_documents: false,
			id_number: '',
			customer_address_city: '',
			customer_address_str: '',
			customer_Building_number: '',
			customer_address_zip_code: '',
			customer_vat_number: '',
			customer_vat_tax: '',
			company_id: null,
			custom_text: '',
		});
		this.setupInitialValidators(6);
		this.addCustomer.markAsPristine();
		this.addCustomer.markAsUntouched();
		this.applySingleCompanyAutoSelect();
	}

	/**
	 * If there is exactly one company in the list, auto-select it (add mode only).
	 * @private
	 */
	private applySingleCompanyAutoSelect(): void {
		if (this.mode !== 'add' || this.companiesList.length !== 1) return;
		const companyId = this.companiesList[0]?.company_id;
		if (companyId != null) {
			this.addCustomer.get('company_id')?.setValue(companyId);
			this.addCustomer.get('company_id')?.updateValueAndValidity();
		}
	}

	/**
	 * Populates the form with customer data for update mode.
	 * @private
	 * @returns void
	 */
	private populateFormForUpdate(): void {
		if (!this.customerData) return;
		
		// Remove +972 prefix from phone if it exists
		let phoneValue = this.customerData.customer_phone || '';
		if (phoneValue.startsWith('+972')) {
			phoneValue = phoneValue.substring(4);
		}

		this.addCustomer.patchValue({
			customer_name_he: this.customerData.customer_name_he || '',
			customer_name_en: this.customerData.customer_name_en || '',
			customer_name_ar: this.customerData.customer_name_ar || '',
			customer_type: this.customerData.customer_type || 6,
			customer_phone: phoneValue,
			customer_email: this.customerData.customer_email || '',
			show_contact_on_documents: !!this.customerData.show_contact_on_documents,
			id_number: this.customerData.id_number || '',
			customer_address_city: this.customerData.customer_address_city || '',
			customer_address_str: this.customerData.customer_address_str || '',
			customer_Building_number: this.customerData.customer_Building_number || '',
			customer_address_zip_code: this.customerData.customer_address_zip_code || '',
			customer_vat_number: this.customerData.customer_vat_number || '',
			customer_vat_tax: this.customerData.customer_vat_tax || '',
			company_id: this.customerData.company_id || null,
			custom_text: this.customerData.custom_text || '',
		});
		
		// Update validators based on customer type
		this.setupInitialValidators(this.customerData.customer_type || 6);
		this.syncCompanyIdWithLoadedList();
	}

	/**
	 * Fetches the list of available customer types from the service.
	 * @returns void
	 */
	getAllCustomerType() {
		this.spinner.show();
		this.service.getAllCustomerType().subscribe({
			next: (res) => {
				this.spinner.hide();
				this.customerTypeData = res;
			},
			error: (error) => {
				this.spinner.hide();
				const detail = this.translate.instant('shared.addCustomerModal.notifications.loadTypeErrorDetail');
				const summary = this.translate.instant('global.notifications.errorSummary');
				this.messageService.add({ severity: 'error', summary: summary, detail: detail });
			},
		});
	}

	/**
	 * Fetches the list of active companies from the service.
	 * @returns void
	 */
	getAllCompanies(): void {
		this.spinner.show();
		this.service.GetCompany().subscribe({
			next: (res: any) => {
				this.spinner.hide();
				const raw = Array.isArray(res) ? res : [];
				this.companiesList = this.normalizeCompaniesList(raw);
				this.syncCompanyIdWithLoadedList();
				this.applySingleCompanyAutoSelect();
			},
			error: (error) => {
				this.spinner.hide();
				if (this.debug) {
					console.error('[DEBUG/AddCustomerModal] Error loading companies:', error);
				}
				const detail =
					this.translate.instant('shared.addCustomerModal.notifications.loadCompaniesErrorDetail') ||
					'Failed to load companies';
				const summary = this.translate.instant('global.notifications.errorSummary');
				this.messageService.add({ severity: 'error', summary: summary, detail: detail });
			},
		});
	}

	/**
	 * Normalize API rows: numeric company_id and a display label for the current language.
	 */
	private normalizeCompaniesList(raw: any[]): any[] {
		return (raw || []).map((c) => {
			const id = c?.company_id != null && c?.company_id !== '' ? Number(c.company_id) : null;
			const label = companyDisplayName(c, this.curentLang);
			return {
				...c,
				company_id: id,
				companyDisplayLabel: label || '—',
			};
		});
	}

	/**
	 * After companies arrive, re-apply company_id so p-dropdown matches options (fixes string/number mismatch).
	 */
	private syncCompanyIdWithLoadedList(): void {
		const control = this.addCustomer?.get('company_id');
		const raw = control?.value;
		if (raw == null || raw === '') {
			return;
		}
		const n = Number(raw);
		if (!Number.isNaN(n)) {
			control.setValue(n, { emitEvent: false });
		}
	}

	/** Company label for the current language, falling back to any saved name. */
	getCompanyName(company: any): string {
		if (!company) {
			return '';
		}
		return companyDisplayName(company, this.curentLang) || (company.companyDisplayLabel || '').trim() || '—';
	}

	/**
	 * Sets up the initial validation rules based on the default customer type.
	 * @param typeValue The initial customer type ID.
	 * @returns void
	 */
	setupInitialValidators(typeValue: number) {
		// const idNumberControl = this.addCustomer.get('id_number');
		// const vatNumberControl = this.addCustomer.get('customer_vat_number');
		const vatTaxControl = this.addCustomer.get('customer_vat_tax');

		// idNumberControl?.clearValidators();
		// vatNumberControl?.clearValidators();
		vatTaxControl?.clearValidators();

		if (typeValue == 5) {
			// idNumberControl?.setValidators([Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[1-9][0-9]*$")]);
			// vatNumberControl?.setValidators([Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[1-9][0-9]*$")]);
		} else if (typeValue == 6) {
			// vatNumberControl?.setValidators([Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]+$")]);
			vatTaxControl?.setValidators([Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]+$")]);
		}

		// idNumberControl?.updateValueAndValidity({ emitEvent: false });
		// vatNumberControl?.updateValueAndValidity({ emitEvent: false });
		vatTaxControl?.updateValueAndValidity({ emitEvent: false });
	}

	/**
	 * Subscribes to changes in the customer type selection and updates validation rules accordingly.
	 * @returns void
	 */
	onCustomerTypeChange() {
		this.addCustomer.get('customer_type')?.valueChanges.subscribe((value) => {
			// const idNumberControl = this.addCustomer.get('id_number');
			// const vatNumberControl = this.addCustomer.get('customer_vat_number');
			const vatTaxControl = this.addCustomer.get('customer_vat_tax');

			// idNumberControl?.clearValidators();
			// idNumberControl?.setValue(null, { emitEvent: false });
			// vatNumberControl?.clearValidators();
			// vatNumberControl?.setValue(null, { emitEvent: false });
			vatTaxControl?.clearValidators();
			vatTaxControl?.setValue(null, { emitEvent: false });

		if (value == 5) {
			// idNumberControl?.setValidators([Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[1-9][0-9]*$")]);
			// vatNumberControl?.setValidators([Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[1-9][0-9]*$")]);
		} else if (value == 6) {
			// vatNumberControl?.setValidators([Validators.required, Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]+$")]);
			vatTaxControl?.setValidators([Validators.minLength(9), Validators.maxLength(9), Validators.pattern("^[0-9]+$")]);
		}

			// idNumberControl?.updateValueAndValidity();
			// vatNumberControl?.updateValueAndValidity();
			vatTaxControl?.updateValueAndValidity();
		});
	}

	/**
	 * Sets up blur event handlers for company fields auto-population.
	 * Auto-population will trigger when user leaves focus from any of the three fields.
	 * @returns void
	 */
	onCompanyIdChange() {
		// Note: The actual blur event handlers will be added in the HTML template
		// This method is kept for compatibility but the logic is now handled via template events
	}

	/**
	 * Handles blur event for company fields and triggers auto-population
	 * @param event The blur event
	 * @param sourceField The field that lost focus
	 */
	onCompanyFieldBlur(event: any, sourceField: string): void {
		const value = event.target.value;
		this.autoPopulateCompanyFields(sourceField, value);
	}

	/**
	 * Auto-populates the other two company fields when one loses focus
	 * Only populates fields that are empty (no existing value)
	 * @param sourceField The field that was changed
	 * @param value The new value
	 */
	private autoPopulateCompanyFields(sourceField: string, value: string) {
		const customerType = this.addCustomer.get('customer_type')?.value;
		
		// Only auto-populate for company type (6)
		if (customerType === 6) {
			const idNumberControl = this.addCustomer.get('id_number');
			const vatNumberControl = this.addCustomer.get('customer_vat_number');
			const vatTaxControl = this.addCustomer.get('customer_vat_tax');
			
			// Helper function to check if a field is empty and can be populated
			const canPopulate = (control: any): boolean => {
				if (!control) return false;
				const currentValue = control.value || '';
				// Only populate if field is empty (no existing value)
				return currentValue === '' || currentValue === null || currentValue === undefined;
			};
			
			// Auto-populate the other two fields based on which field lost focus
			if (sourceField === 'id_number') {
				// رقم الضريبي lost focus, update the other two only if they are empty
				if (vatNumberControl && canPopulate(vatNumberControl)) {
					vatNumberControl.setValue(value || '', { emitEvent: false });
				}
				if (vatTaxControl && canPopulate(vatTaxControl)) {
					vatTaxControl.setValue(value || '', { emitEvent: false });
				}
			} else if (sourceField === 'customer_vat_number') {
				// رقم تسجيل المصلحة التجارية lost focus, update the other two only if they are empty
				if (idNumberControl && canPopulate(idNumberControl)) {
					idNumberControl.setValue(value || '', { emitEvent: false });
				}
				if (vatTaxControl && canPopulate(vatTaxControl)) {
					vatTaxControl.setValue(value || '', { emitEvent: false });
				}
			} else if (sourceField === 'customer_vat_tax') {
				// رقم مشتغل lost focus, update the other two only if they are empty
				if (idNumberControl && canPopulate(idNumberControl)) {
					idNumberControl.setValue(value || '', { emitEvent: false });
				}
				if (vatNumberControl && canPopulate(vatNumberControl)) {
					vatNumberControl.setValue(value || '', { emitEvent: false });
				}
			}
		}
	}

	/**
	 * Handles the submission of the customer form.
	 * Validates the form, prepares the data model, and calls the service to save or update the customer.
	 * Shows success or error notifications.
	 * @returns void
	 */
	addedData() {
		if (this.addCustomer.invalid) {
			this.addCustomer.markAllAsTouched();
			const detail = this.translate.instant('shared.addCustomerModal.notifications.invalidFormDetail');
			const summary = this.translate.instant('global.notifications.errorSummary');
			this.messageService.add({ severity: 'error', summary: summary, detail: detail });
			return;
		}

		if (this.mode === 'update') {
			this.updateCustomerData();
		} else {
			this.createCustomerData();
		}
	}

	/**
	 * Creates a new customer.
	 * @private
	 */
	private createCustomerData() {
		const formValue = this.addCustomer.value;
		if (!formValue.customer_name_en) {
			formValue.customer_name_en = formValue.customer_name_he;
		}
		if (!formValue.customer_name_ar) {
			formValue.customer_name_ar = formValue.customer_name_he;
		}
		if (!formValue.customer_address_city) {
			formValue.customer_address_city = ' ';
		}
		if (!formValue.customer_address_str) {
			formValue.customer_address_str = ' ';
		}
		if (!formValue.customer_Building_number) {
			formValue.customer_Building_number = ' ';
		}
		if (!formValue.customer_address_zip_code) {
			formValue.customer_address_zip_code = ' ';
		}

		/**
		 * Prepare the model as Customer type
		 */
		const model: Customer = {
			customer_name_he: formValue.customer_name_he,
			customer_name_en: formValue.customer_name_en,
			customer_name_ar: formValue.customer_name_ar,
			customer_type: formValue.customer_type,
			customer_phone: formValue.customer_phone ? `+972${formValue.customer_phone}` : '',
			customer_email: formValue.customer_email || null,
			show_contact_on_documents: !!formValue.show_contact_on_documents,
			id_number: formValue.id_number || null,
			customer_address_city: formValue.customer_address_city,
			customer_address_str: formValue.customer_address_str,
			customer_Building_number: formValue.customer_Building_number,
			customer_address_zip_code: formValue.customer_address_zip_code,
			customer_vat_number: formValue.customer_vat_number || null,
			customer_vat_tax: formValue.customer_vat_tax || null,
			created_by: JSON.parse(localStorage.getItem('user_id') || 'null'),
			updated_by: null,
			createdDate: '',
			updatedDate: '',
			customer_id: 0,
			customer_address_str_en: '',
			customer_address_str_ar: '',
			customer_address_city_en: '',
			customer_address_city_ar: '',
			customer_status: 1,
			merchant_id: JSON.parse(localStorage.getItem('merchant_id') || 'null'),
			company_id: formValue.company_id || null,
			custom_text: null,
		};

		this.submitNewCustomer(model, false);
	}

	private submitNewCustomer(model: Customer, confirmed: boolean) {
		const endpoint = confirmed ? AddCustomerEndPoient.CONFIRM : AddCustomerEndPoient.POST;
		this.spinner.show();
		this.service.Post(endpoint, model).subscribe({
			next: (res: any) => {
				this.spinner.hide();
				if (res?.status === 200) {
					this.notifyCustomerCreated(res);
					return;
				}
				if (!confirmed && this.isDuplicateVatWarning(res)) {
					this.askDuplicateVatConfirmation(res, model);
					return;
				}
				this.notifyCustomerCreateError(res);
			},
			error: (error) => {
				this.spinner.hide();
				const body = error?.error;
				if (!confirmed && this.isDuplicateVatWarning(body)) {
					this.askDuplicateVatConfirmation(body, model);
					return;
				}
				this.notifyCustomerCreateError(body);
			}
		});
	}

	private isDuplicateVatWarning(body: any): boolean {
		return !!body && (body.status === 400 || body.warning === true) && body.requiresConfirmation === true;
	}

	private existingCustomerName(data: any): string {
		const ar = String(data?.customer_name_ar ?? '').trim();
		const en = String(data?.customer_name_en ?? '').trim();
		const he = String(data?.customer_name_he ?? '').trim();
		if (this.curentLang === 'en') return en || he || ar;
		if (this.curentLang === 'he') return he || en || ar;
		return ar || he || en;
	}

	private askDuplicateVatConfirmation(body: any, model: Customer) {
		const name = this.existingCustomerName(body?.data);
		const text = name
			? this.translate.instant('shared.addCustomerModal.notifications.duplicateVatExisting', { name })
			: '';
		Swal.fire({
			icon: 'warning',
			title: this.translate.instant('shared.addCustomerModal.notifications.duplicateVatQuestion'),
			text,
			showCancelButton: true,
			confirmButtonText: this.translate.instant('shared.addCustomerModal.notifications.duplicateVatConfirm'),
			cancelButtonText: this.translate.instant('shared.addCustomerModal.notifications.duplicateVatCancel'),
			reverseButtons: this.curentLang !== 'en',
			customClass: { container: 'add-customer-swal' },
		}).then((result) => {
			if (result.isConfirmed) {
				this.submitNewCustomer(model, true);
			}
		});
	}

	private notifyCustomerCreated(res: any) {
		let summary = this.translate.instant('global.notifications.successSummary');
		if (summary === 'global.notifications.successSummary') {
			summary = this.curentLang === 'ar' ? 'نجاح' : (this.curentLang === 'en' ? 'Success' : 'הצלחה');
		}
		const detail = (this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'en' ? res.messageEn : res.messageHe))
			|| this.translate.instant('shared.addCustomerModal.notifications.addSuccessDetail');
		this.messageService.add({ severity: 'success', summary, detail });
		this.onSave.emit(res.data);
		this.resetForm();
	}

	private notifyCustomerCreateError(res: any) {
		const detail = (this.curentLang === 'ar' ? res?.messageAr : (this.curentLang === 'en' ? res?.messageEn : res?.messageHe))
			|| this.translate.instant('shared.addCustomerModal.notifications.addErrorFallbackDetail');
		setTimeout(() => {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('global.notifications.errorSummary'),
				detail,
			});
		}, 0);
	}

	/**
	 * Updates an existing customer.
	 * @private
	 */
	private updateCustomerData() {
		const formValue = this.addCustomer.value;
		
		// Handle phone number formatting for update
		let customerPhone = formValue.customer_phone;
		if (customerPhone?.startsWith('0')) {
			customerPhone = customerPhone.substring(1);
		}

		// Auto-fill other language names if not provided
		const customerNameHe = formValue.customer_name_he;
		if (!formValue.customer_name_en) {
			formValue.customer_name_en = customerNameHe;
		}
		if (!formValue.customer_name_ar) {
			formValue.customer_name_ar = customerNameHe;
		}

		const updateModel = {
			customer_name_he: formValue.customer_name_he,
			customer_name_en: formValue.customer_name_en,
			customer_name_ar: formValue.customer_name_ar,
			customer_address_str: formValue.customer_address_str || '',
			customer_address_city: formValue.customer_address_city || '',
			customer_address_zip_code: formValue.customer_address_zip_code || '',
			customer_Building_number: formValue.customer_Building_number || '',
			customer_phone: customerPhone ? `+972${customerPhone}` : '',
			customer_email: formValue.customer_email || null,
			show_contact_on_documents: !!formValue.show_contact_on_documents,
			customer_vat_number: formValue.customer_vat_number,
			customer_vat_tax: formValue.customer_vat_tax,
			id_number: formValue.id_number,
			customer_type: Number(formValue.customer_type),
			company_id: formValue.company_id || null,
			updated_by: JSON.parse(localStorage.getItem('user_id') || 'null'),
			custom_text: this.customerData?.custom_text || null,
		};

		this.spinner.show();
		this.service.updateCustomersById(this.customerData.customer_id, updateModel).subscribe({
			next: (res: any) => {
				this.spinner.hide();
				if (res.status === 200) {
					let summary = this.translate.instant('global.notifications.successSummary');
					// Fallback if translation returns the key itself
					if (summary === 'global.notifications.successSummary') {
						summary = this.curentLang === 'ar' ? 'نجاح' : (this.curentLang === 'en' ? 'Success' : 'הצלחה');
					}
					const detail = this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'en' ? res.messageEn : res.messageHe)
						|| this.translate.instant('shared.addCustomerModal.notifications.updateSuccessDetail');
					this.messageService.add({ severity: 'success', summary: summary, detail: detail });
					this.onSave.emit(res.data);
					this.dismissModal();
				} else {
					const detail = (this.curentLang === 'ar' ? res.messageAr : (this.curentLang === 'en' ? res.messageEn : res.messageHe))
						|| this.translate.instant('shared.addCustomerModal.notifications.updateErrorFallbackDetail');
					setTimeout(() => {
						this.messageService.add({ severity: 'error', summary: this.translate.instant('global.notifications.errorSummary'), detail: detail });
					}, 0);
				}
			},
			error: (error) => {
				this.spinner.hide();
				const detail = this.translate.instant('shared.addCustomerModal.notifications.updateErrorGenericDetail');
				const summary = this.translate.instant('global.notifications.errorSummary');
				setTimeout(() => {
					this.messageService.add({ severity: 'error', summary: summary, detail: detail });
				}, 0);
			}
		});
	}

	/**
	 * Retrieves the localized name of a customer type based on its ID.
	 * @param typeId The ID of the customer type.
	 * @returns The localized name of the customer type, or an empty string if not found.
	 */
	getCustomerTypeName(typeId: number): string {
		const selectedType = this.customerTypeData.find(
			(item) => item.lookup_detail_id === typeId
		);
		return selectedType
			? this.curentLang === 'en'
				? selectedType.lookupDetailEnName
				: this.curentLang === 'ar'
					? selectedType.lookupDetailArName
					: selectedType.lookupDetailHeName
			: '';
	}

	/**
	 * True when contact display is enabled but neither phone nor email is filled.
	 */
	get showNoContactDetailsWarning(): boolean {
		if (!this.addCustomer) {
			return false;
		}
		if (!this.addCustomer.get('show_contact_on_documents')?.value) {
			return false;
		}
		const phone = (this.addCustomer.get('customer_phone')?.value || '').toString().trim();
		const email = (this.addCustomer.get('customer_email')?.value || '').toString().trim();
		return !phone && !email;
	}

	/**
	 * Utility function to allow only numeric input in certain fields.
	 * Prevents non-numeric key presses.
	 * @param event The keyboard event.
	 * @returns True if the key press is allowed (numeric), false otherwise.
	 */
	isNumber(event: KeyboardEvent): boolean {
		const charCode = event.which ? event.which : event.keyCode;
		if (charCode > 31 && (charCode < 48 || charCode > 57)) {
			event.preventDefault();
			return false;
		} else {
			return true;
		}
	}

	/**
	 * Handles the focusout event for the modal
	 * @param event The focusout event
	 */
	onModalFocusOut(event: FocusEvent): void {
		// Add your custom logic here
		// Examples: validation, auto-save, data processing, etc.
		
if (this.addCustomer.invalid && this.addCustomer.dirty) {
			}
	}

	/**
	 * Dismisses the modal, resets the form, and emits the onClose event.
	 * @returns void
	 */
	dismissModal() {
		this.resetForm();
		this.onClose.emit();
	}
}
